import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Switch,
  Textarea,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  FormErrorMessage,
  Icon,
  Text,
  Flex,
  useColorModeValue,
  Tooltip,
  Box,
} from '@chakra-ui/react';
import { MdInfo, MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { CloseIcon } from '@chakra-ui/icons';
import { isValidUrl } from 'helpers/helper';

const CInput = ({
  label,
  type = 'text',
  value,
  mb = "24px",
  onChange,
  required = false,
  placeholder = '',
  validationRules = {},
  tooltipContent = null,
  validated = false,
  maxDecimals = null,
  maxValue = null,
  minValue = null,
  symbol = null,
  minAge = 16,
  ...props
}) => {
  const [show, setShow] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const brandStars = useColorModeValue('red.500', 'red.400');
  const textColor = useColorModeValue('navy.700', 'white');

  const handleClick = () => setShow(!show);

  const validateInput = (value) => {
    if (required && (value === undefined || value === null || value === '' || value.trim() === '')) {
      return `${label} is required.`;
    }
    if (value && validationRules.minLength && value.length < validationRules.minLength) {
      return `${label} must be at least ${validationRules.minLength} ${type === 'number' ? 'digits' : 'characters'}.`;
    }
    if (value && validationRules.maxLength && value.length > validationRules.maxLength) {
      return `${label} must not exceed ${validationRules.maxLength} ${type === 'number' ? 'digits' : 'characters'}.`;
    }
    if (value && validationRules.url === true && !isValidUrl(value)) {
      return `URL must start with "http://" or "https://".`;
    }

    if (
      type === 'number' &&
      (
        (maxValue && parseFloat(value) > maxValue) ||
        (minValue && parseFloat(value) < minValue)
      )
    ) {
      return `Please provide a valid ${label}.`;
    }

    if (type === 'date') {
      const selectedDate = new Date(value);
      const today = new Date();
      const minAllowedDate = new Date(
        today.getFullYear() - minAge,
        today.getMonth(),
        today.getDate()
      );

      if (selectedDate > minAllowedDate) {
        return `${label} must be greater than 16 years.`;
      }
    }

    return '';
  };

  useEffect(() => {
    if (validated) {
      setErrorMessage(validateInput(value));
    }
  }, [validated, value, minValue, maxValue]);

  const handleChange = (inputValue) => {
    let newValue = inputValue;

    if (type === 'number' && maxDecimals !== null) {
      const regex = new RegExp(`^\\d*(\\.\\d{0,${maxDecimals}})?$`);
      if (!regex.test(inputValue)) return;
    }

    const error = validateInput(newValue);
    setErrorMessage(error);
    onChange(newValue);
  };

  return (
    <FormControl isInvalid={!!errorMessage} mb={mb}>
      {type === 'switch' ? (
        <Flex align="center" justify="space-between">
          <FormLabel
            ms="4px"
            fontSize="l"
            fontWeight="900"
            color={textColor}
            mb="0"
            mr="10px"
            minWidth={"fit-content"}
          >
            {label} {required && <Text as="span" color={brandStars}>*</Text>}
          </FormLabel>
          <Switch
            isChecked={value}
            onChange={(e) => handleChange(e.target.checked)}
            colorScheme="brand"
            size="md"
            {...props}
          />
        </Flex>
      ) : (
        <>
          <FormLabel
            display="flex"
            ms="4px"
            fontSize="l"
            fontWeight="900"
            color={textColor}
            mb="8px"
          >
            {label} {required && <Text as="span" color={brandStars}>*</Text>}
            {tooltipContent && (
              <Tooltip
                hasArrow
                placement="top-start"
                bg="gray.800"
                color="white"
                px="20px"
                py="16px"
                borderRadius="md"
                boxShadow="lg"
                label={
                  <Box position="relative">
                    <Flex justify="space-between" align="center" mb="8px">
                      <Text fontWeight="bold" fontSize="md">
                        {tooltipContent.header}
                      </Text>
                      {/* <CloseIcon
                        boxSize={3}
                        cursor="pointer"
                        onClick={onClose}
                        _hover={{ color: "red.500" }}
                      /> */}
                    </Flex>
                    <Text fontSize="sm" lineHeight="1.4">
                      {tooltipContent.content}
                    </Text>
                  </Box>
                }
              >

                <Text fontSize="md" color="gray.500" cursor="pointer">
                  <Icon as={MdInfo} />
                </Text>
              </Tooltip>
            )}
          </FormLabel>



          {type === 'textarea' ? (
            <Textarea
              variant="auth"
              fontSize="sm"
              ms={{ base: '0px', md: '0px' }}
              placeholder={placeholder}
              value={value}
              fontWeight="500"
              size="lg"
              rows={5}
              border={'2px solid gray'}
              borderColor={errorMessage ? 'red.400' : 'inherit'}
              onChange={(e) => handleChange(e.target.value)}
              {...props}
            />
          ) : (
            <InputGroup size="md">
              {symbol && (
                <InputLeftElement pointerEvents="none" color="gray.400" d="flex" alignItems="center" height="100%" pl="2">
                  <Text>{symbol}</Text>
                </InputLeftElement>
              )}
              <Input
                variant="auth"
                fontSize="sm"
                ms={{ base: '0px', md: '0px' }}
                type={type === 'password' && show ? 'text' : type}
                placeholder={placeholder}
                value={value}
                fontWeight="500"
                size="lg"
                borderColor={errorMessage ? 'red.500' : 'inherit'}
                onChange={(e) => handleChange(e.target.value)}
                step={type === 'number' && maxDecimals ? Math.pow(10, -maxDecimals) : undefined}
                {...props}
              />
              {type === 'password' && (
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    color={'gray.400'}
                    onClick={handleClick}
                    cursor="pointer"
                  />
                </InputRightElement>
              )}
            </InputGroup>
          )}
        </>
      )}
      {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
};

export default CInput;
